import { ReactElement, useEffect, useState } from 'react'
import { AxiosResponse } from 'axios'
import { SurveyQuestion, SurveyQuestionChoice } from '../../models/global.types'
import { Button, LoadingSpinner, Text } from '../atoms'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'
import HexChoiceButton from '../atoms/HexChoiceButton/HexChoiceButton'
import AnonymityTags from './AnonymityTags'
import NaloxoneCompletion from './NaloxoneCompletion'
import apiServices from '../../libs/services'
import { splitChoicesIntoColumns } from './Survey'

type NaloxoneSurveyStep = 'question' | 'tags' | 'complete'

export interface NaloxoneSurveyApi {
  getSurvey: () => Promise<AxiosResponse>
  start: () => Promise<AxiosResponse>
  submitAnswer: (responseId: string, questionId: string, data: { choiceIds?: string[] }) => Promise<AxiosResponse>
  setAnonTag: (responseId: string, anonTag: string) => Promise<AxiosResponse>
  complete: (responseId: string) => Promise<AxiosResponse>
}

// Public QR-path adapter, no sign-in. The Account header is set from the hostname.
export const publicNaloxoneSurveyApi: NaloxoneSurveyApi = {
  getSurvey: async () => await apiServices.getNaloxoneSurvey(),
  start: async () => await apiServices.startNaloxoneSurvey(),
  submitAnswer: async (responseId, questionId, data) => await apiServices.submitNaloxoneAnswer(responseId, questionId, data),
  setAnonTag: async (responseId, anonTag) => await apiServices.setNaloxoneAnonTag(responseId, anonTag),
  complete: async (responseId) => await apiServices.completeNaloxoneSurvey(responseId)
}

interface NaloxoneSurveyProps {
  api?: NaloxoneSurveyApi
  onFinish?: () => void
}

const kbControlsLayer = 'survey'
const surveyButtonId = 'naloxone-survey-button-next'
const surveyPageClass = 'bg-dopage-black min-h-screen flex flex-col items-center justify-center font-primary px-4 md:px-0'
const surveyCardClass = 'bg-dopage-charcoalGray p-6 md:p-8 w-full max-w-[600px] h-[85dvh] md:h-[750px] flex flex-col items-center justify-center overflow-y-auto md:mx-4'

const getQuestionInputId = (questionId: string): string => `naloxone-survey-input-${questionId}`
const getQuestionLabelId = (questionId: string): string => `naloxone-survey-label-${questionId}`

// AC wording: two "Yes - ..." choices and one "No - ...". Matched by text since choice IDs vary per upload.
export const isYesChoice = (choice: SurveyQuestionChoice): boolean =>
  choice.text.trim().toLowerCase().startsWith('yes')

// The axios interceptor resolves HTTP errors instead of throwing, so status must be checked explicitly.
const isErrorResponse = (res: AxiosResponse | null | undefined): boolean =>
  res == null || (typeof res.status === 'number' && res.status >= 400)

function NaloxoneSurvey ({ api = publicNaloxoneSurveyApi, onFinish }: NaloxoneSurveyProps): ReactElement {
  const keyboardControls = useKeyboardControls()
  const [step, setStep] = useState<NaloxoneSurveyStep>('question')
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null)
  const [question, setQuestion] = useState<SurveyQuestion | null>(null)
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    keyboardControls.pushActiveLayer(kbControlsLayer)
    return () => keyboardControls.resetDefaultLayer()
  }, [keyboardControls])

  useEffect(() => {
    if (step !== 'question' || question === null) return
    keyboardControls.setFocusedElement(getQuestionInputId(question.id), kbControlsLayer)
  }, [step, question, keyboardControls])

  // No consent screen, so this loads on mount. The response itself is created later, on the
  // first Next click, so an abandoned page load leaves no orphaned response.
  useEffect(() => {
    const loadSurvey = async (): Promise<void> => {
      try {
        const surveyRes = await api.getSurvey()
        if (isErrorResponse(surveyRes)) {
          setError('Survey not found.')
          return
        }
        const questions = surveyRes.data?.questions as SurveyQuestion[] | undefined
        if (questions == null || questions.length !== 1) {
          setError('Survey is misconfigured.')
          return
        }
        const firstQuestion = questions[0]
        const yesChoiceCount = firstQuestion.choices.filter(isYesChoice).length
        // AC content is fixed at 2 Yes + 1 No - a near-miss must fail loudly, not silently misroute.
        if (firstQuestion.choices.length !== 3 || yesChoiceCount !== 2) {
          setError('Survey is misconfigured.')
          return
        }
        setQuestion(firstQuestion)
      } catch (err: any) {
        console.error('Failed to load survey.', err)
        setError('Failed to load survey.')
      } finally {
        setLoading(false)
      }
    }

    void loadSurvey()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleQuestionNext = async (): Promise<void> => {
    if (question === null || selectedChoiceId === null) return
    const choice = question.choices.find(c => c.id === selectedChoiceId)
    if (choice === undefined) return

    setLoading(true)
    setError(null)
    try {
      const startRes = await api.start()
      const responseId = startRes?.data?.surveyResponseId as string | undefined
      if (isErrorResponse(startRes) || responseId == null) {
        setError('Failed to start survey.')
        return
      }

      const answerRes = await api.submitAnswer(responseId, question.id, { choiceIds: [selectedChoiceId] })
      if (isErrorResponse(answerRes)) {
        setError('Failed to submit answer.')
        return
      }

      setSurveyResponseId(responseId)

      if (isYesChoice(choice)) {
        setStep('tags')
      } else {
        const completeRes = await api.complete(responseId)
        if (isErrorResponse(completeRes)) {
          setError('Failed to complete survey.')
          return
        }
        setStep('complete')
      }
    } catch (err: any) {
      console.error('Failed to submit answer.', err)
      setError('Failed to submit answer.')
    } finally {
      setLoading(false)
    }
  }

  const handleTagsSubmit = async (anonTag: string): Promise<void> => {
    if (surveyResponseId === null) return
    setLoading(true)
    setError(null)
    try {
      const tagRes = await api.setAnonTag(surveyResponseId, anonTag)
      if (isErrorResponse(tagRes)) {
        setError('Failed to submit survey.')
        return
      }
      const completeRes = await api.complete(surveyResponseId)
      if (isErrorResponse(completeRes)) {
        setError('Failed to complete survey.')
        return
      }
      setStep('complete')
    } catch (err: any) {
      console.error('Failed to submit survey.', err)
      setError('Failed to submit survey.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <LoadingSpinner className='w-32 md:w-48' />
        </div>
      </div>
    )
  }

  if (error !== null) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text={error} size='LG' className='text-red-500' />
        </div>
      </div>
    )
  }

  if (step === 'tags') {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <AnonymityTags onSubmit={handleTagsSubmit} />
        </div>
      </div>
    )
  }

  if (step === 'complete') {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <NaloxoneCompletion onContinue={onFinish} />
        </div>
      </div>
    )
  }

  // step === 'question'
  if (question === null) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text='No survey found' size='LG' className='text-dopage-lightGreen' />
        </div>
      </div>
    )
  }

  const { column1, column2 } = splitChoicesIntoColumns(question.choices)

  const renderChoice = (choice: SurveyQuestionChoice): ReactElement => {
    const inputId = `${getQuestionInputId(question.id)}-${choice.id}`
    return (
      <HexChoiceButton
        key={choice.id}
        id={inputId}
        label={choice.text}
        isSelected={selectedChoiceId === choice.id}
        isRequired
        type='radio'
        onClick={() => setSelectedChoiceId(choice.id)}
        layer={kbControlsLayer}
      >
        <span aria-hidden='true' className='text-dopage-lightGreen text-base md:text-lg'>{choice.text}</span>
      </HexChoiceButton>
    )
  }

  return (
    <div className={surveyPageClass}>
      <div className='bg-dopage-charcoalGray w-full max-w-[600px] h-[85dvh] md:h-[750px] flex flex-col relative overflow-hidden md:mx-4'>
        <div className='p-6 md:p-8 flex-1 flex flex-col overflow-y-auto'>
          <Text
            text={question.text}
            size='LG'
            id={getQuestionLabelId(question.id)}
            className='text-dopage-lightGreen mb-6 md:mb-8 text-lg md:text-xl'
          />

          <div className='flex-1'>
            <div role='radiogroup' aria-labelledby={getQuestionLabelId(question.id)} className='flex flex-col md:flex-row gap-4 md:gap-6'>
              <div className='flex flex-col gap-4 md:gap-5 flex-1'>
                {column1.map(renderChoice)}
              </div>
              {column2.length > 0 && (
                <div className='flex flex-col gap-4 md:gap-5 flex-1'>
                  {column2.map(renderChoice)}
                </div>
              )}
            </div>
          </div>

          <div className='flex justify-center mt-6 md:mt-0'>
            <ControlHighlight
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(surveyButtonId)}
              highlightEffect={HighlightEffect.ButtonHexOutline}
            >
              <KeyboardControl layer={kbControlsLayer}>
                <Button
                  id={surveyButtonId}
                  disabled={selectedChoiceId === null}
                  onClick={() => { void handleQuestionNext() }}
                  className='px-8 py-3 font-semibold'
                >
                  <span>Next</span>
                </Button>
              </KeyboardControl>
            </ControlHighlight>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NaloxoneSurvey
